import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { PhoneNumberInput } from '@/components/PhoneNumberInput.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { emailSchema, phoneNumberSchema } from '@/lib/validation-schemas.ts';

const formId = 'insurance-waitlist-form';
const formSchema = z.object({
  phoneNumber: phoneNumberSchema,
  email: emailSchema,
});
export type FormData = z.infer<typeof formSchema>;

const useStore = create<FormData>()(
  devtools(
    persist(
      () => ({
        phoneNumber: '',
        email: '',
      }),
      {
        name: 'InsuranceWaitlistForm-storage',
        storage: createJSONStorage(() => sessionStorage),
      }
    ),
    { name: 'InsuranceWaitlistForm-storage', enabled: import.meta.env.MODE !== 'test' }
  )
);

type Props = {
  onSubmit: (insurance: FormData) => void;
};

export function InsuranceWaitlistForm({ onSubmit }: Props) {
  const store = useStore();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: store,
  });

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      id={formId}
      className="grid grid-cols-1 gap-8 sm:gap-12"
    >
      <FieldGroup>
        <Controller
          name="phoneNumber"
          control={form.control}
          render={({ field, fieldState }) => {
            const inputId = `${formId}-phoneNumber`;

            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={inputId}>Mobile phone</FieldLabel>
                <PhoneNumberInput {...field} id={inputId} aria-invalid={fieldState.invalid} />
                {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
              </Field>
            );
          }}
        />

        <Controller
          name="email"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={`${formId}-email`}>Email</FieldLabel>
              <Input
                {...field}
                id={`${formId}-email`}
                placeholder="name@youremail.com"
                aria-invalid={fieldState.invalid}
              />
              {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>

      <Button className="w-full sm:w-auto" type="submit">
        Join waitlist
      </Button>
    </form>
  );
}
